import React, { useEffect, useMemo } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { POIS, CITY_CENTER, areaCoords } from '../lib/geo';

const projectIcon = L.divIcon({
  className: '',
  html: '<div style="width:24px;height:24px;background:#C1440E;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,.45)"></div>',
  iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -24],
});
const poiIcon = L.divIcon({
  className: '',
  html: '<div style="width:12px;height:12px;background:#14532d;border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
  iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || !points.length) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(points, { padding: [45, 45], maxZoom: 14 });
  }, [points, map]);
  return null;
}

const AreaMap = ({ areaName, projects }) => {
  const data = useMemo(() => {
    const ac = areaCoords(areaName);
    const city = (ac && ac.city) || 'Dubai';
    const center = ac || CITY_CENTER[city];
    const markers = [];
    let fallbackIdx = 0;
    projects.forEach((p) => {
      const lat = Number(p.lat);
      const lng = Number(p.lng);
      const exact = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
      let base = exact ? { lat, lng } : (areaCoords(p.area) || center);
      if (!exact) {
        // slight deterministic jitter so projects pinned at the same area centre separate out
        const col = fallbackIdx % 3;
        const row = Math.floor(fallbackIdx / 3);
        base = { lat: base.lat + row * 0.0026, lng: base.lng + col * 0.0026 };
        fallbackIdx += 1;
      }
      markers.push({ id: p.id, name: p.name, area: p.area, pos: [base.lat, base.lng] });
    });
    const pois = POIS.filter((x) => x.city === city);
    const points = [...markers.map((m) => m.pos), ...pois.map((x) => [x.lat, x.lng])];
    if (!points.length) points.push([center.lat, center.lng]);
    return { city, center, markers, pois, points };
  }, [areaName, projects]);

  return (
    <div className="relative z-0 rounded-2xl overflow-hidden border border-gobra-sand" style={{ height: 440 }} data-testid="area-map">
      <MapContainer center={[data.center.lat, data.center.lng]} zoom={12} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, OpenStreetMap contributors'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
        />
        <FitBounds points={data.points} />
        {data.markers.map((m) => (
          <Marker key={m.id} position={m.pos} icon={projectIcon}>
            <Popup>
              <Link to={`/project/${m.id}`} className="font-semibold text-gobra-terracotta">{m.name}</Link>
              {m.area ? <><br />{m.area}</> : null}
            </Popup>
          </Marker>
        ))}
        {data.pois.map((p) => (
          <Marker key={p.name} position={[p.lat, p.lng]} icon={poiIcon}>
            <Popup>{p.name}</Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default AreaMap;
